import React from 'react';
import { Card } from '@/components/ui/card';
import { 
  FileSpreadsheet, 
  TrendingUp, 
  AlertTriangle, 
  Clock 
} from 'lucide-react';
import { PROJECT_STATUS_COLORS, TASK_STATUS_COLORS } from './utils';

function StatCard({ label, value, icon: Icon, iconBg, valueColor }) {
  return (
    <Card className="p-5 border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${valueColor || 'text-gray-900'}`}>
            {value}
          </p>
        </div>
        <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${valueColor || 'text-gray-600'}`} />
        </div>
      </div>
    </Card>
  );
}

function StatusBar({ status, count, total, colorConfig }) {
  const percentage = (count / total) * 100;
  return (
    <div className="flex items-center gap-4">
      <div className="w-32 text-sm text-gray-600">{colorConfig.label}</div>
      <div className="flex-1">
        <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className={`h-full ${colorConfig.bg} transition-all duration-500`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      <div className="w-12 text-right font-semibold text-gray-900">{count}</div>
    </div>
  );
}

export function OverviewStats({ overview }) {
  return (
    <div className="space-y-6">
      {/* Project Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          label="Total de Projetos"
          value={overview?.projects?.total || 0}
          icon={FileSpreadsheet}
          iconBg="bg-emerald-100"
          valueColor="text-emerald-600"
        />
        <StatCard
          label="Progresso Médio"
          value={`${overview?.projects?.average_progress || 0}%`}
          icon={TrendingUp}
          iconBg="bg-blue-100"
          valueColor="text-blue-600"
        />
        <StatCard
          label="Tarefas Atrasadas"
          value={overview?.tasks?.overdue || 0}
          icon={AlertTriangle}
          iconBg="bg-red-100"
          valueColor="text-red-600"
        />
        <StatCard
          label="Vencem esta Semana"
          value={overview?.tasks?.due_this_week || 0}
          icon={Clock}
          iconBg="bg-orange-100"
          valueColor="text-orange-600"
        />
      </div>

      {/* Projects and Tasks by Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Projetos por Status</h3>
          <div className="space-y-4">
            {Object.entries(overview?.projects?.by_status || {}).map(([status, count]) => {
              const total = overview?.projects?.total || 1;
              const colorConfig = PROJECT_STATUS_COLORS[status]
                || { bg: 'bg-gray-300', text: 'text-gray-700', label: status || 'Desconhecido' };
              return (
                <StatusBar 
                  key={status}
                  status={status}
                  count={count}
                  total={total}
                  colorConfig={colorConfig}
                />
              );
            })}
          </div>
        </Card>

        <Card className="p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tarefas por Status</h3>
          <div className="space-y-4">
            {Object.entries(overview?.tasks?.by_status || {}).map(([status, count]) => {
              const total = overview?.tasks?.total || 1;
              const colorConfig = TASK_STATUS_COLORS[status] || TASK_STATUS_COLORS['not_started'];
              return (
                <StatusBar 
                  key={status}
                  status={status}
                  count={count}
                  total={total}
                  colorConfig={colorConfig}
                />
              );
            })}
          </div>
        </Card>
      </div>

      {/* Task Deadline Summary */}
      <Card className="p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumo de Prazos</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <p className="text-3xl font-bold text-red-600">{overview?.tasks?.overdue || 0}</p>
            <p className="text-sm text-red-700 mt-1">Atrasadas</p>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <p className="text-3xl font-bold text-orange-600">{overview?.tasks?.due_this_week || 0}</p>
            <p className="text-sm text-orange-700 mt-1">Esta Semana</p>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <p className="text-3xl font-bold text-yellow-600">{overview?.tasks?.due_this_month || 0}</p>
            <p className="text-sm text-yellow-700 mt-1">Este Mês</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-3xl font-bold text-gray-600">{overview?.tasks?.total || 0}</p>
            <p className="text-sm text-gray-700 mt-1">Total de Tarefas</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default OverviewStats;
